import { PopoverPlacement } from "@idui/react-popover";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useRtl } from "@ryde/redux";
import {
  allowTimeEditInMainPickerSelector,
  formatCalendarDateToNormalized,
  isMidnight,
  MIN_DATE_VALUE,
  RydeUiUnit,
  topCalendarDatesAtomFamily,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { FramerTooltip } from "../../tooltips";
import {
  TopCalendarDateTimeTextStyled,
  TopCalendarTimeStyled,
} from "./top-calendar.styled";

interface ITopCalendarDateTimeTextProps {
  entity: RydeUiUnit;
}

export const TopCalendarDateTimeText: FC<ITopCalendarDateTimeTextProps> = ({
  entity,
}) => {
  const activeDatesUnit = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { formatDateNormalized, formatTimeNormalized, dateFormat } =
    useFormats();
  const dateFrom = activeDatesUnit?.date1 || MIN_DATE_VALUE;
  const dateTo = activeDatesUnit?.date2 || MIN_DATE_VALUE;
  const { timezone } = useTimezone();
  const dateFromFormatted = formatDateNormalized(dateFrom);
  const dateToFormatted = formatCalendarDateToNormalized(
    dateTo,
    dateFormat,
    timezone,
  );
  const timeFromFormatted = formatTimeNormalized(dateFrom);
  const allowTimeEditInMainPicker = useRecoilValue(
    allowTimeEditInMainPickerSelector,
  );
  const isMidnightDateFrom = isMidnight(dateFrom, timezone);
  const isTimeFromShow = allowTimeEditInMainPicker ? true : !isMidnightDateFrom;
  const isDateToHidden = dateFromFormatted === dateToFormatted;
  const isTimeToVisibleAndDateToHidden =
    isDateToHidden && allowTimeEditInMainPicker;
  const timeToFormatted = formatTimeNormalized(dateTo);
  const { t } = useTranslation();
  const { isRtl } = useRtl();

  return (
    <FramerTooltip
      content={t("general.calendar.OPEN_CALENDAR_WIDGET")}
      placement={PopoverPlacement.bottom}
    >
      <TopCalendarDateTimeTextStyled>
        <div className={`date-time-wrapper`}>
          <span className="date">{dateFromFormatted}</span>

          <span
            className={`${isTimeToVisibleAndDateToHidden && !isRtl ? "revers-direction" : ""}`}
          >
            {isTimeToVisibleAndDateToHidden && (
              <>
                <TopCalendarTimeStyled className="time-to">
                  {timeToFormatted}
                </TopCalendarTimeStyled>
                <span className="dash">-</span>
              </>
            )}

            {isTimeFromShow && (
              <TopCalendarTimeStyled className="time-from">
                {timeFromFormatted}
              </TopCalendarTimeStyled>
            )}
          </span>
        </div>

        {!isDateToHidden && (
          <>
            <span className="dash">-</span>

            <div className="date-time-wrapper">
              <span className="date">{dateToFormatted}</span>

              {allowTimeEditInMainPicker && (
                <TopCalendarTimeStyled>{timeToFormatted}</TopCalendarTimeStyled>
              )}
            </div>
          </>
        )}
      </TopCalendarDateTimeTextStyled>
    </FramerTooltip>
  );
};
